import React, { useCallback, useState } from 'react';
import styles from './move-popup.module.scss';
import { RenderBinderSelector } from 'src/components/knowledge-content/knowledge-content-editor/components/hub/render-binder-selector';
import { Checkbox } from 'antd';
import { getLang } from 'shared/store/selectors/lang.selector';
import { useSelector } from 'react-redux';
import { STORE_ENTITY_TYPES } from 'shared/constants';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';
import { getSelectedWorkspaceId } from 'shared/store/selectors/workspace.selector';

const MovePopupComponent = React.memo(function MovePopupComponent({
  item,
  categoryId,
  disabledIds = [],
  doMove = () => {},
  doCreateLink = () => {},
  onFinish = () => {},
  showToast = () => {},
  enableCreateLink = false,
  isOpen = false,
  generateBreadcrumbs,
  getCategoriesById,
  getCategoryById,
  getRoot,
  isCategoryMove = false,
  isLinkMove = false,
  isSection = () => {},
  entity = STORE_ENTITY_TYPES.KNOWLEDGE,
  parentId,
  disabledWorkspaceIds = [],
}) {
  item = item ?? {};
  const lang = useSelector(getLang('KNOWLEDGE_MOVE'));
  const initialWorkspaceId = useSelector(getSelectedWorkspaceId);
  const [isCreateLink, setCreateLink] = useState(false);
  const [newCategoryId, setNewCategoryId] = useState(null);
  const [linkOwnerIds, setLinkOwnerIds] = useState([]);
  const [targetWorkspaceId, setTargetWorkspaceId] = useState(initialWorkspaceId);

  const newCategory = useSelector(state => getCategoryById(state[entity], { id: newCategoryId }));

  // Reset state when popup opens
  React.useEffect(() => {
    if (isOpen) {
      setLinkOwnerIds([]);
      setTargetWorkspaceId(initialWorkspaceId);
      setCreateLink(false);
    }
  }, [isOpen, initialWorkspaceId]);

  const selectBinder = useCallback(binderAndWorskpace => {
    setNewCategoryId(binderAndWorskpace?.binderId);
    if (binderAndWorskpace?.targetWorkspaceId) {
      setTargetWorkspaceId(binderAndWorskpace.targetWorkspaceId);
    }
  }, []);

  const handleWorkspaceChange = useCallback(workspaceId => {
    setTargetWorkspaceId(workspaceId);
    setLinkOwnerIds([]);
  }, []);

  const toggleCreateLink = useCallback(() => {
    setCreateLink(isCreate => !isCreate);
  }, []);

  const dismiss = useCallback(() => {
    setNewCategoryId(null);
    onFinish();
  }, [onFinish]);

  const handleOwnerChange = useCallback((_name, value) => {
    setLinkOwnerIds(value || []);
  }, []);

  const handleMove = useCallback(async () => {
    if (isCreateLink) {
      await doCreateLink({
        id: item.id,
        items: [item],
        linkOwnerIds,
        categoryId: newCategoryId,
      });
    } else {
      await doMove({
        id: item.id,
        item,
        sourceId: isCategoryMove ? parentId : categoryId,
        categoryId: newCategoryId,
      });
    }

    showToast(newCategory);
    onFinish();
  }, [
    doMove,
    doCreateLink,
    onFinish,
    item,
    isCreateLink,
    linkOwnerIds,
    newCategoryId,
    categoryId,
    parentId,
    isCategoryMove,
    newCategory,
    showToast,
  ]);

  const submitDisabled = isCreateLink && linkOwnerIds.length === 0;
  const modalTitle = isLinkMove ? lang.LINK_TITLE : lang.TITLE;

  const getSubmitText = () => {
    if (isCreateLink) return lang.LINK;
    if (isLinkMove) return lang.MOVE_LINK;
    return lang.MOVE;
  };
  const submitText = getSubmitText();

  return (
    <RenderBinderSelector
      parentId={parentId}
      title={modalTitle}
      subtitle={item.title}
      submitButtonText={submitText}
      filterCategories={disabledIds}
      selectTarget={selectBinder}
      onSubmit={handleMove}
      isOpen={isOpen}
      onClose={dismiss}
      getRoot={getRoot}
      getCategoriesById={getCategoriesById}
      getCategoryById={getCategoryById}
      generateBreadcrumbs={generateBreadcrumbs}
      isSection={isSection}
      entity={entity}
      isCategoryMove={isCategoryMove}
      submitDisabled={submitDisabled}
      onWorkspaceChange={handleWorkspaceChange}
      disabledWorkspaceIds={disabledWorkspaceIds}
      disabledWorkspaceTooltip={lang.CANNOT_MOVE_TO_WORKSPACE_THAT_MIRRORS}
      pathParts={item?.path?.split(',')}>
      {enableCreateLink && (
        <div className={styles.checkbox}>
          <Checkbox onChange={toggleCreateLink} checked={isCreateLink} />
          <label className={styles.checkboxLabel}>{lang.CHECKBOX}</label>
        </div>
      )}
      {isCreateLink && (
        <MultipleOwnersSelector
          label={lang.OWNER}
          size="large"
          value={linkOwnerIds}
          error={newCategoryId && linkOwnerIds.length === 0 ? lang.OWNER_REQUIRED : null}
          placeholder={lang.OWNER_PLACEHOLDER}
          name="linkOwnerIds"
          onChange={handleOwnerChange}
          borderRadius={4}
          workspaceId={targetWorkspaceId}
          required
        />
      )}
    </RenderBinderSelector>
  );
});

export { MovePopupComponent };
